import type {
  DiagnosticAttentionBoard,
  DiagnosticAttentionParams,
  DiagnosticChange,
  DiagnosticChangeListParams,
  DiagnosticConnectorCall,
  DiagnosticConnectorCallListParams,
  DiagnosticErrorCodeCount,
  DiagnosticFiringAlert,
  DiagnosticIncomingWebhook,
  DiagnosticIncomingWebhookDetail,
  DiagnosticIncomingWebhookListParams,
  DiagnosticInfrastructure,
  DiagnosticList,
  DiagnosticOutgoingWebhook,
  DiagnosticOutgoingWebhookListParams,
  DiagnosticOutgoingWebhookSummary,
  DiagnosticPanel,
  DiagnosticPayment,
  DiagnosticPaymentListParams,
  DiagnosticQueueItem,
  DiagnosticQueueListParams,
  DiagnosticQueueSummary,
  DiagnosticResolvedId,
  DiagnosticRevealRequest,
  DiagnosticRevealResponse,
  DiagnosticRouterLog,
  DiagnosticRouterLogParams,
  DiagnosticTrace,
} from '../types';
import type { RequestFn } from '../../client';

/**
 * The diagnostic console — everything the router did, without a
 * shell.
 *
 * Cross-merchant by construction: one call answers across every merchant on the
 * deployment. The backend gates that on the `Diagnostic` permission, which is
 * granted to DeloPay's internal staff roles alone, which is why this lives in
 * `@delopay/sdk/internal` and never on the merchant-facing client.
 *
 * ## Two shapes worth knowing before you call anything
 *
 * **Lists answer {@link DiagnosticList}**, which carries `hidden_by_environment`
 * beside the rows. That number is what lets a UI say "612 test-mode rows are
 * hidden" rather than leaving an operator unable to tell an empty tab from a
 * filtered one.
 *
 * **Two reads answer {@link DiagnosticPanel} instead** — {@link routerLogs} and
 * {@link alerts} — because their sources can be down independently of the
 * router. A panel is `available` with data or `unavailable` with a reason, and
 * that distinction is the whole point: **an unreachable log store must never be
 * rendered as "no errors found"**, and a `T[]` gives a caller no way to tell the
 * two apart.
 *
 * ## The environment filter
 *
 * Every list takes `environment`. Omitting it is read by the backend as
 * `'live'`, never as `'all'` — a caller that forgot to say gets the safer of the
 * two, and a test payment never appears in a view that reads as live.
 */
export class Diagnostic {
  constructor(private readonly request: RequestFn) {}

  /**
   * The ranked attention board — the page's landing state, with nothing typed.
   *
   * Always carries `checks_run` and `vitals`, so an empty `items` reads as
   * "checked these things at 14:02, all clear" rather than as a panel that
   * failed to load. A check that could not run is **absent** from `checks_run`
   * rather than silently counted as clear.
   */
  async attention(body?: DiagnosticAttentionParams): Promise<DiagnosticAttentionBoard> {
    return this.request('POST', '/admin-portal/diagnostic/attention', { body: body ?? {} });
  }

  /**
   * Provider webhooks received, across every merchant.
   *
   * `source_verified: false` selects rows that failed verification **or** never
   * reached it — the deliveries that failed before verification was attempted
   * are exactly the ones an operator opens this tab for.
   */
  async listIncomingWebhooks(
    body?: DiagnosticIncomingWebhookListParams,
  ): Promise<DiagnosticList<DiagnosticIncomingWebhook>> {
    return this.request('POST', '/admin-portal/diagnostic/webhooks/incoming', {
      body: body ?? {},
    });
  }

  /**
   * One delivery in full: masked headers and body, the verification verdict,
   * and the router log lines for its request id.
   *
   * `router_logs` is `null` — not `[]` — when the log store is unreachable, and
   * `router_logs_unavailable` then says why. Render the first as *unavailable*,
   * never as "the router logged nothing".
   */
  async getIncomingWebhook(eventId: string): Promise<DiagnosticIncomingWebhookDetail> {
    return this.request(
      'GET',
      `/admin-portal/diagnostic/webhooks/incoming/${encodeURIComponent(eventId)}`,
    );
  }

  /** Shop webhooks sent, across every merchant, with each event's attempts folded in. */
  async listOutgoingWebhooks(
    body?: DiagnosticOutgoingWebhookListParams,
  ): Promise<DiagnosticList<DiagnosticOutgoingWebhook>> {
    return this.request('POST', '/admin-portal/diagnostic/webhooks/outgoing', {
      body: body ?? {},
    });
  }

  /**
   * The four tiles above the shop-webhooks table.
   *
   * Counted under the same window and environment as the table but **without**
   * its delivery filter — the tiles say what the window holds, and applying the
   * filter would make every tile agree with whichever one is selected.
   */
  async outgoingWebhookSummary(
    body?: DiagnosticOutgoingWebhookListParams,
  ): Promise<DiagnosticOutgoingWebhookSummary> {
    return this.request('POST', '/admin-portal/diagnostic/webhooks/outgoing/summary', {
      body: body ?? {},
    });
  }

  /**
   * Connector calls the router made.
   *
   * List rows carry no payload. Fetch one with {@link getConnectorCall} for the
   * masked request and response.
   */
  async listConnectorCalls(
    body?: DiagnosticConnectorCallListParams,
  ): Promise<DiagnosticList<DiagnosticConnectorCall>> {
    return this.request('POST', '/admin-portal/diagnostic/connector-calls', { body: body ?? {} });
  }

  /** One connector call, with its masked payloads. */
  async getConnectorCall(id: string): Promise<DiagnosticConnectorCall> {
    return this.request(
      'GET',
      `/admin-portal/diagnostic/connector-calls/${encodeURIComponent(id)}`,
    );
  }

  /**
   * Router logs, proxied from the deployment's log store.
   *
   * Answers a {@link DiagnosticPanel}. Check `status` before reading `data`:
   * `'unavailable'` means the store did not answer, which is the opposite of
   * "there were no errors" and must be rendered as such.
   */
  async routerLogs(
    body?: DiagnosticRouterLogParams,
  ): Promise<DiagnosticPanel<DiagnosticList<DiagnosticRouterLog>>> {
    return this.request('POST', '/admin-portal/diagnostic/logs', { body: body ?? {} });
  }

  /**
   * Payments that are stuck or failed.
   *
   * Stuck means non-terminal for longer than `stuck_minutes`; failed means it
   * ended without succeeding inside the window. Omitting `stuck_minutes` turns
   * the first arm off rather than widening it.
   */
  async listPayments(
    body?: DiagnosticPaymentListParams,
  ): Promise<DiagnosticList<DiagnosticPayment>> {
    return this.request('POST', '/admin-portal/diagnostic/payments', { body: body ?? {} });
  }

  /**
   * The error codes in this window, each with its count in the previous window
   * of equal length.
   *
   * `previous_count: null` means the code did not appear before — render it as
   * "new" rather than as a fall to zero.
   */
  async paymentErrorCodes(body?: DiagnosticPaymentListParams): Promise<DiagnosticErrorCodeCount[]> {
    return this.request('POST', '/admin-portal/diagnostic/payments/error-codes', {
      body: body ?? {},
    });
  }

  /**
   * Everything about one payment, in one call: the payment, its attempts, its
   * status history, its client context, and what it produced — connector calls,
   * webhooks both ways, and scheduler jobs.
   *
   * Read `diagnostic_capture` before rendering an empty section. A payment with
   * no connector calls and a deployment that never recorded any are different
   * facts, and the second must not read as the first.
   *
   * Typed loosely on purpose: the payment, attempts, status-history and
   * client-context halves are the merchant-facing shapes this package already
   * exports, and re-declaring them here would give consumers two names for one
   * thing that could then drift apart.
   */
  async getPayment(paymentId: string): Promise<Record<string, unknown>> {
    return this.request(
      'GET',
      `/admin-portal/diagnostic/payments/${encodeURIComponent(paymentId)}`,
    );
  }

  /** Scheduler tasks, most overdue first. */
  async listQueues(body?: DiagnosticQueueListParams): Promise<DiagnosticList<DiagnosticQueueItem>> {
    return this.request('POST', '/admin-portal/diagnostic/queues', { body: body ?? {} });
  }

  /**
   * The four tiles above the queues table, plus the approvals count.
   *
   * `pending_approvals` is a **different** queue from `pending_count`: a stalled
   * scheduler needs a consumer, a full approvals queue needs a person, and
   * folding them into one number would point an operator at the wrong fix.
   */
  async queueSummary(): Promise<DiagnosticQueueSummary> {
    return this.request('GET', '/admin-portal/diagnostic/queues/summary');
  }

  /**
   * Replicas and their dependencies.
   *
   * `build_mismatch` is computed server-side rather than left for a caller to
   * notice by comparing strings: one replica in four answering differently
   * explains a whole class of "it only fails sometimes" incident, and is
   * invisible unless something says it out loud.
   */
  async infrastructure(): Promise<DiagnosticInfrastructure> {
    return this.request('GET', '/admin-portal/diagnostic/infrastructure');
  }

  /**
   * Firing alerts, proxied from Alertmanager.
   *
   * Answers a {@link DiagnosticPanel} for the same reason {@link routerLogs}
   * does: a strip that renders empty because Alertmanager is down says "nothing
   * is firing", which is the most dangerous sentence this page could print.
   */
  async alerts(): Promise<DiagnosticPanel<DiagnosticFiringAlert[]>> {
    return this.request('GET', '/admin-portal/diagnostic/alerts');
  }

  /** Config edits and admin actions inside the window. */
  async listChanges(body?: DiagnosticChangeListParams): Promise<DiagnosticList<DiagnosticChange>> {
    return this.request('POST', '/admin-portal/diagnostic/changes', { body: body ?? {} });
  }

  /**
   * What a pasted id is, and whether we hold it.
   *
   * `kind` and `found` are separate answers on purpose. A well-shaped id that
   * matches nothing comes back `found: false` with the three `reasons` and
   * their fixes — render that, never an empty table, which reads as "no such
   * payment" when the real answer is "outside the window" or "it is a test
   * payment".
   */
  async resolveId(id: string): Promise<DiagnosticResolvedId> {
    return this.request('GET', `/admin-portal/diagnostic/resolve/${encodeURIComponent(id)}`);
  }

  /**
   * One id across every source, as an ordered span list.
   *
   * Accepts a payment, attempt, event or request id. Sources that could not be
   * read are named in `unavailable_sources` rather than left out: a waterfall
   * with an unexplained gap reads as a period when nothing happened.
   */
  async trace(id: string): Promise<DiagnosticTrace> {
    return this.request('GET', `/admin-portal/diagnostic/trace/${encodeURIComponent(id)}`);
  }

  /**
   * Unmask one field of one object.
   *
   * **Writes an audit row** naming the operator, the field and the object,
   * before the value is returned — a reveal that could not be audited does not
   * happen. Needs a dashboard JWT holding `DiagnosticReveal`; an admin API key
   * cannot call it, because an API key names no person.
   *
   * `reason` is required and is stored verbatim.
   */
  async reveal(body: DiagnosticRevealRequest): Promise<DiagnosticRevealResponse> {
    return this.request('POST', '/admin-portal/diagnostic/reveal', { body });
  }
}
